<script>
import moment from 'moment';
import rules from '@/helpers/validation-rules';

export default {
  name: 'DateOfBirthSelector',

  props: {
    dayValue: Number,
    monthValue: Number,
    yearValue: Number,
    readonly: {
      type: Boolean,
      default: false,
    },
    activeRules: {
      type: Boolean,
      default: true,
    }
  },

  data: () => ({
    rules,

    d: '',
    m: '',
    y: '',

    items: [
      { text: 'Jan', value: 1 },
      { text: 'Feb', value: 2 },
      { text: 'Mar', value: 3 },
      { text: 'Apr', value: 4 },
      { text: 'May', value: 5 },
      { text: 'Jun', value: 6 },
      { text: 'Jul', value: 7 },
      { text: 'Aug', value: 8 },
      { text: 'Sep', value: 9 },
      { text: 'Oct', value: 10 },
      { text: 'Nov', value: 11 },
      { text: 'Dec', value: 12 },
    ],

    showMonthSelector: false,
    isValid: false,
    errorMessage: '',
    isInvalid: false
  }),

  computed: {
    yearRules() {
      return [
        rules.minLength(4),
        rules.maxLength(4),
        rules.min(
          new Date().getFullYear() - 100,
          'Are you older than 100 years?'
        ),
        (v) =>
          !v || v < new Date().getFullYear() || 'Are you born in the future?',
      ];
    },

    dateIsValid() {
      return (
        !this.$refs.day?.hasError &&
        !this.$refs.month?.hasError &&
        !this.$refs.year?.hasError &&
        this.userIsOlderThan18
      );
    },

    dateOfBirth() {
      return moment(`${this.y}/${this.m}/${this.d}`, 'YYYY/MM/DD');
    },

    parsedDate() {
      return new Date(`${this.y}/${this.m}/${this.d}`);
    },

    isDateOfBirthValid() {
      return (
        this.dateOfBirth.isValid() &&
        this.parsedDate.getFullYear() == this.y &&
        this.parsedDate.getMonth() == this.m - 1 &&
        this.parsedDate.getDate() == this.d
      );
    },

    userIsOlderThan18() {
      if (!this.isDateOfBirthValid) return false;
      return moment().diff(this.dateOfBirth, 'years') >= 18;
    },

    maxDate() {
      return new Date().getFullYear();
    },

    isBooted() {
      return this.d && this.m && this.y && `${this.y}`.length === 4;
    },
  },

  watch: {
    dateIsValid: {
      handler() {
        this.$emit('update', this.y && this.m && this.d);
      },
      immediate: true,
      deep: true,
    },

    dayValue: {
      handler(val) {
        this.d = val;
      },
      immediate: true,
    },

    monthValue: {
      handler(val) {
        this.m = val;
      },
      immediate: true,
    },

    yearValue: {
      handler(val) {
        this.y = val;
      },
      immediate: true,
    },

    userIsOlderThan18(val) {
      if (val) {
        this.errorMessage = null;
      } else if (!this.isDateOfBirthValid) {
        this.errorMessage = 'Invalid date.';
      } else if (moment().diff(this.dateOfBirth, 'years') < 18) {
        this.errorMessage = `Must be at least <strong>18</strong> years old.`;
      }
    },
  },

  methods: {
    inputValue(key, value) {
      this[key] = Math.abs(value);
    },
    getDate() {
      const checkDate = new Date(`${this.y}/${this.m}/${this.d}`);
      if (this.$refs.form.validate() && moment().diff(`${this.m}/${this.d}/${this.y}`, 'years') >= 18 &&
        (checkDate.getFullYear() === this.y
          && checkDate.getMonth() === this.m - 1
          && checkDate.getDate() === this.d)
      ) {
        return `${this.y}-${this.m}-${this.d}`;
      } else {
        this.isInvalid = true;
        this.errorMessage = 'Invalid date.';
        return false;
      }
    },
  },
};
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    lazy-validation
  >
    <v-row class="px-3">
      <v-col class="pl-0 py-0 pr-0" cols="3">
        <v-text-field
          ref="day"
          v-model="d"
          v-mask="'##'"
          :readonly="readonly"
          :rules="activeRules ? [
            rules.maxLength(2),
            rules.max(31, 'Day value must be less than 31'),
          ] : []"
          persistent-placeholder
          placeholder="DD"
          solo
        />
      </v-col>
      <v-col class="monthField py-0 px-0 px-sm-1" cols="6">
        <div id="monthSelector" class="position-relative">
          <v-select
            ref="month"
            v-model="m"
            :items="items"
            :menu-props="{
              offsetY: true,
            }"
            :readonly="readonly"
            append-icon="mdi-chevron-down"
            attach="#monthSelector"
            color="white"
            persistent-placeholder
            placeholder="Month"
            solo
          >
          </v-select>
        </div>
      </v-col>
      <v-col class="pr-0 py-0 pl-0" cols="3">
        <v-text-field
          ref="year"
          v-model="y"
          v-mask="'####'"
          :max="maxDate"
          :min="1900"
          :readonly="readonly"
          :rules="activeRules ? yearRules : []"
          class="yearInput"
          maxLength="4"
          persistent-placeholder
          placeholder="YYYY"
          solo
        />
      </v-col>
      <v-col v-show="errorMessage && isBooted && !userIsOlderThan18" cols="12">
        <v-alert
          border="left"
          class="font-weight-semi font-size-18"
          color="main-light"
          type="error"
        >
          <span v-html="errorMessage" />
        </v-alert>
      </v-col>
    </v-row>
  </v-form>
</template>

<style lang="scss" scoped>
.monthField::v-deep {
  #monthSelector {
    .v-input {
      .v-input__control {
        .v-select__slot {
          .v-select__selections {
            .v-select__selection {
              padding: 0 8px 4px !important;

              @media screen and (max-width: 1919px) {
                padding: 0 8px 4px !important;
                font-size: 1rem !important;
              }
            }
          }
        }
      }
    }
  }
}

.yearInput::v-deep {
  .v-input__control {
    .v-input__slot {
      .v-text-field__slot {
        input {
          padding: 0 8px 4px !important;

          @media screen and (max-width: 1919px) {
            padding: 0 8px 4px !important;
            font-size: 1rem !important;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 600px) {
  #monthSelector {
    margin: 0 4px;
  }
}
</style>
